import { styled } from '@mui/material/styles'
import React, { useEffect, useRef } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useTranslation } from 'react-i18next'
import { Link, useLoaderData } from 'react-router'
import useAuth from '../../hooks/useAuth'
import { useTheme } from '@mui/material/styles'
import { useAppLocale } from '../../i18n/localeUtils'

import { Button, Grid, LinearProgress, Divider as MuiDivider, Stack, Typography } from '@mui/material'

import { spacing } from '@mui/system'
import { normalizedWarrantName } from '@finance-lab/utils/shared'
import WarrantDetailsView from './WarrantDetailsView'
import { isFutureWarrant, isPreviousWarrant } from './WarrantsView'
import { WatchListButton } from '../components/WatchListButton'
import { EntityNoteButton } from '../components/EntityNoteButton'
import { WarrantListItem } from '@finance-lab/utils/types'

const Divider = styled(MuiDivider)(spacing)

function getDescriptiveName(warrant: WarrantListItem) {
    if (!warrant) return ''
    return normalizedWarrantName(warrant)
}

export function WarrantStatus({ warrant, details = false }: { warrant: WarrantListItem; details?: boolean }) {
    const theme = useTheme()
    const { t } = useTranslation()
    if (!warrant) return null
    const now = new Date()

    let status: { color: string; text: string } | null = null

    if (warrant.cancelled)
        status = { color: theme.palette.entityStatus.cancelled, text: t('pages.warrants.status.cancelled') }
    else if (isFutureWarrant(now, warrant))
        status = { color: theme.palette.entityStatus.incomplete, text: t('pages.warrants.status.upcoming') }
    else if (isPreviousWarrant(now, warrant))
        status = { color: theme.palette.entityStatus.complete, text: t('pages.warrants.status.completed') }
    else status = { color: theme.palette.entityStatus.incomplete, text: t('pages.warrants.status.ongoingSubscription') }

    if (!status) return null

    if (!details) return <span>{status.text}</span>

    return (
        <Stack
            direction='row'
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            <div
                style={{
                    height: '10px',
                    width: '10px',
                    margin: 0,
                    backgroundColor: status.color,
                    borderRadius: '50%',
                }}
            />
            <Typography>{status.text}</Typography>
        </Stack>
    )
}

function WarrantDetails() {
    const { user } = useAuth()
    const { t } = useTranslation()
    const { language } = useAppLocale()
    const warrant = useLoaderData()
    const isInitialLanguageRender = useRef(true)

    useEffect(() => {
        if (isInitialLanguageRender.current) {
            isInitialLanguageRender.current = false
            return
        }

        window.location.reload()
    }, [language])

    return (
        <>
            <Helmet title={t('site.warrants.detailsTitle', { name: getDescriptiveName(warrant) })} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                    fontStyle: warrant?.cancelled ? 'italic' : 'normal',
                }}
            >
                {warrant?.cancelled ? `${t('pages.warrants.status.cancelled')} ` : ''}{' '}
                {t('site.warrants.detailsTitle', { name: getDescriptiveName(warrant) })}
                <WatchListButton entityId={warrant.id} isInWatchList={!!warrant.isInWatchList} type='Warrant' />
                <EntityNoteButton entityId={warrant.id} type='warrant' />
            </Typography>
            {user?.groups?.includes('superadmin') && (
                <>
                    <Button variant='text' to={`/admin/warrants/${warrant?.id}`} LinkComponent={Link}>
                        {t('site.admin.editLink')}
                    </Button>

                    <Button variant='text' to={`/admin/pr-company?companyId=${warrant.companyId}`} LinkComponent={Link}>
                        {t('site.admin.viewAdminPr')}
                    </Button>
                </>
            )}
            <Typography
                variant='body2'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                {t('pages.warrants.status.label')} <WarrantStatus warrant={warrant} />
            </Typography>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={12}>
                    {!warrant && <LinearProgress />}
                    {warrant && <WarrantDetailsView warrant={warrant} />}
                </Grid>
            </Grid>
        </>
    )
}

export default WarrantDetails
